import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import cn from "classnames";
import { useGetCategoriesQuery } from "src/store/apis/giftcards";
import { getSelectedClientId } from "src/store/slices/client";
import { track } from "@amplitude/analytics-browser";
import useEmblaCarousel from "embla-carousel-react";
import { useDirection } from "src/i18n/direction";
import { DefaultCheckIcon } from "src/assets/Icons";

type CategoryOption = { label: string; value: string };

type ChipsProps = {
  value?: string;
  onChange?: (value: string) => void;
};

export function useCategoryOptions() {
  const { t, i18n } = useTranslation();
  const clientId = useSelector(getSelectedClientId);
  // clientId only scopes the cache entry (getCategories is shared with Discounts); it is not sent in the body
  const { data, isFetching, error } = useGetCategoriesQuery({
    Page: 1,
    PageSize: 1000000,
    lang: i18n.language,
    clientId,
  });

  const options: CategoryOption[] = [
    {
      label: t("Giftcards.allCategories"),
      value: "",
    },
    ...(data?.categories
      ?.filter((cat) => cat.name)
      .map((cat) => ({
        label: cat.name,
        value: cat.code,
      })) ?? []),
  ];

  return { options, isFetching, error, hasData: !!data?.categories?.length };
}

export const CategoryOptionButtons: FC<ChipsProps & { options: CategoryOption[]; isFetching: boolean }> = ({ value, onChange, options, isFetching }) => {
  if (isFetching)
    return (
      <>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-11 w-full animate-pulse rounded-md bg-background-subtle md:h-9 md:w-24 md:rounded-lg" />
        ))}
      </>
    );

  return (
    <>
      {options.map((option) => {
        const isSelected = option.value === (value ?? "");
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              track("Category Selected", { ...option });
              onChange?.(option.value);
            }}
            className={cn(
              "capitalize relative flex w-full shrink-0 cursor-pointer items-center justify-between rounded-sm p-3 text-start font-semibold whitespace-nowrap transition-colors",
              "md:w-auto md:justify-center md:rounded-full md:px-8 md:py-2 md:text-sm border",
              isSelected
                ? "text-font-active md:bg-background-hover"
                : "font-normal text-font-minimal hover:bg-background-hover md:hover:bg-background-hover",
            )}
          >
            {option.label}
            {isSelected && <DefaultCheckIcon className="h-4 w-4 md:hidden" />}
          </button>
        );
      })}
    </>
  );
};

export const CategoryChips: FC<ChipsProps & { options: CategoryOption[]; isFetching: boolean }> = ({ value, onChange, options, isFetching }) => {
  const direction = useDirection();
  const [emblaRef] = useEmblaCarousel({ loop: false, axis: "x", align: "start", dragThreshold: 6, inViewThreshold: 0.1, active: true, direction, dragFree:true });

  return (
    <div ref={emblaRef} className="overflow-hidden w-full">
      <div className="flex gap-2">
        <CategoryOptionButtons value={value} onChange={onChange} options={options} isFetching={isFetching} />
      </div>
    </div>
  );
};
